import React, { useState, useEffect, useCallback, useMemo } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { BarChart3, Users, TrendingUp, Clock, CheckCircle2, AlertTriangle, Calendar, Factory, ListTodo, GitBranch, ClipboardList, Search, ArrowUpDown, ArrowUp, ArrowDown, Target, X } from 'lucide-react';
import { formatDate, formatDateShort } from '@/lib/utils';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' }
];

const currentYear = new Date().getFullYear();
const YEARS = [currentYear - 1, currentYear, currentYear + 1];

// Color functions for NEGATIVE scoring (higher % = worse)
const getScoreColor = (score, isNegative = false) => {
  if (isNegative) {
    // For negative metrics (% not done), lower is better
    if (score <= 10) return 'text-green-600';
    if (score <= 25) return 'text-yellow-600';
    if (score <= 40) return 'text-orange-600';
    return 'text-red-600';
  }
  // For positive metrics (weighted score), higher is better
  if (score >= 90) return 'text-green-600';
  if (score >= 70) return 'text-yellow-600';
  if (score >= 50) return 'text-orange-600';
  return 'text-red-600';
};

const getScoreBadge = (score, isNegative = false) => {
  if (isNegative) {
    // For negative metrics, lower is better
    if (score <= 10) return 'bg-green-100 text-green-800';
    if (score <= 25) return 'bg-yellow-100 text-yellow-800';
    if (score <= 40) return 'bg-orange-100 text-orange-800';
    return 'bg-red-100 text-red-800';
  }
  if (score >= 90) return 'bg-green-100 text-green-800';
  if (score >= 70) return 'bg-yellow-100 text-yellow-800';
  if (score >= 50) return 'bg-orange-100 text-orange-800';
  return 'bg-red-100 text-red-800';
};

// Helper functions for negative scoring (lower is better) - v2 MIS model
const getNegativeScoreColor = (pct) => {
  if (pct === 0) return 'text-green-600';
  if (pct <= 10) return 'text-green-500';
  if (pct <= 25) return 'text-yellow-600';
  if (pct <= 50) return 'text-orange-600';
  return 'text-red-600';
};

const getNegativeScoreBg = (pct) => {
  if (pct === 0) return 'bg-green-50';
  if (pct <= 10) return 'bg-green-50';
  if (pct <= 25) return 'bg-yellow-50';
  if (pct <= 50) return 'bg-orange-50';
  return 'bg-red-50';
};

// Escalation badge styles
const getEscalationBadge = (tier) => {
  switch(tier) {
    case 'delayed': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    case 'critical': return 'bg-orange-100 text-orange-800 border-orange-300';
    case 'extreme_critical': return 'bg-red-100 text-red-800 border-red-300';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const ProgressBar = ({ value, label, color = 'blue' }) => {
  const colors = {
    blue: 'bg-blue-600',
    green: 'bg-green-600',
    yellow: 'bg-yellow-600',
    red: 'bg-red-600',
    orange: 'bg-orange-600'
  };
  
  const barColor = value >= 90 ? colors.green : 
                   value >= 70 ? colors.yellow : 
                   value >= 50 ? colors.orange : colors.red;
  
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className={getScoreColor(value)}>{value}%</span>
      </div>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div 
          className={`h-full ${barColor} transition-all duration-300`}
          style={{ width: `${Math.min(value, 100)}%` }}
        />
      </div>
    </div>
  );
};

export default function MISDashboardPage() {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [availableWeeks, setAvailableWeeks] = useState([]);
  const [employees, setEmployees] = useState([]);
  
  // Filter states
  const [periodType, setPeriodType] = useState('week');
  const [selectedWeek, setSelectedWeek] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  
  // Employee breakdown states
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [employeeBreakdown, setEmployeeBreakdown] = useState(null);
  
  // Production Efficiency states
  const [productionEfficiency, setProductionEfficiency] = useState(null);
  const [efficiencyMetric, setEfficiencyMetric] = useState('planned_vs_actual');
  const [loadingEfficiency, setLoadingEfficiency] = useState(false);
  
  // Weighted Scoring states
  const [weightedScores, setWeightedScores] = useState(null);
  const [blendFactor, setBlendFactor] = useState(0.5);
  const [loadingWeighted, setLoadingWeighted] = useState(false);
  
  // Employee search and sorting states
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  const [scoreSortOrder, setScoreSortOrder] = useState('asc'); // 'asc' = best first (lowest score), 'desc' = worst first (highest score)
  const [employeeTableSortOrder, setEmployeeTableSortOrder] = useState('asc'); // 'asc' = best first (lowest score), 'desc' = worst first
  const [employeeTableSortColumn, setEmployeeTableSortColumn] = useState('weekly'); // 'weekly' or 'overall'

  // Helper function to calculate weighted score from employee data
  // Formula: (Weighted % ND × 60%) + (Weighted % NDOT × 40%)
  // Handles both weighted-scores-v2 (root level) and employee-productivity-v2 (overall object) data structures
  const calculateWeightedScore = (emp) => {
    if (!emp) return null;
    // Check root level first (weighted-scores-v2 structure), then overall object (employee-productivity-v2)
    const pctND = emp.pct_not_done ?? emp.overall?.pct_not_done ?? 0;
    const pctNDOT = emp.pct_not_done_on_time ?? emp.overall?.pct_not_done_on_time ?? 0;
    return parseFloat(((pctND * 0.6) + (pctNDOT * 0.4)).toFixed(1));
  };

  // Weekly Targets states (Phase 4) - Inline meeting mode
  const [weeklyTargets, setWeeklyTargets] = useState([]);
  const [currentWeekTargets, setCurrentWeekTargets] = useState({
    target_nd_percent: '',
    target_ndot_percent: ''
  });
  const [loadingTargets, setLoadingTargets] = useState(false);
  const [savingTargets, setSavingTargets] = useState(false);

  // Task Details Dialog states
  const [taskDetailsOpen, setTaskDetailsOpen] = useState(false);
  const [taskDetailsCategory, setTaskDetailsCategory] = useState(null);
  const [taskDetailsData, setTaskDetailsData] = useState(null);
  const [taskDetailsLoading, setTaskDetailsLoading] = useState(false);
  const [taskDetailsFilter, setTaskDetailsFilter] = useState('all'); // 'all', 'done', 'ndot', 'nd'
  
  // Escalation Popup states
  const [escalationPopupOpen, setEscalationPopupOpen] = useState(false);
  const [escalationPopupTier, setEscalationPopupTier] = useState(null);
  const [escalationPopupData, setEscalationPopupData] = useState(null);
  const [escalationPopupLoading, setEscalationPopupLoading] = useState(false);

  // Helper function to get week date range string (ISO week - Jan 4 method)
  const getWeekDateRange = (weekNum, year) => {
    // ISO week calculation: Week 1 contains Jan 4
    const jan4 = new Date(year, 0, 4);
    const dayOfWeek = jan4.getDay();
    // Find Monday of Week 1 (go back to Monday)
    const mondayOfWeek1 = new Date(jan4);
    mondayOfWeek1.setDate(jan4.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
    
    // Calculate the start of the requested week (Monday)
    const weekStart = new Date(mondayOfWeek1);
    weekStart.setDate(mondayOfWeek1.getDate() + (weekNum - 1) * 7);
    
    // Calculate the end of the week (Sunday)
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    
    const formatDate = (d) => `${d.getDate()} ${d.toLocaleString('en', { month: 'short' })}`;
    return `${formatDate(weekStart)}-${formatDate(weekEnd)}`;
  };

  // Get current calendar week (based on today's date, not selected week)
  const getCurrentCalendarWeek = () => {
    const now = new Date();
    const year = now.getFullYear();
    
    // ISO week calculation: Week 1 contains Jan 4
    const jan4 = new Date(year, 0, 4);
    const dayOfWeek = jan4.getDay();
    const mondayOfWeek1 = new Date(jan4);
    mondayOfWeek1.setDate(jan4.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
    
    // Calculate days since Monday of Week 1
    const diffTime = now.getTime() - mondayOfWeek1.getTime();
    const diffDays = Math.floor(diffTime / (24 * 60 * 60 * 1000));
    const weekNum = Math.floor(diffDays / 7) + 1;
    
    // Handle year boundary (if weekNum < 1, it's last week of previous year)
    if (weekNum < 1) {
      // Calculate for previous year
      const prevYear = year - 1;
      const prevJan4 = new Date(prevYear, 0, 4);
      const prevDayOfWeek = prevJan4.getDay();
      const prevMondayOfWeek1 = new Date(prevJan4);
      prevMondayOfWeek1.setDate(prevJan4.getDate() - (prevDayOfWeek === 0 ? 6 : prevDayOfWeek - 1));
      const prevDiffTime = now.getTime() - prevMondayOfWeek1.getTime();
      const prevDiffDays = Math.floor(prevDiffTime / (24 * 60 * 60 * 1000));
      return { week: Math.floor(prevDiffDays / 7) + 1, year: prevYear };
    }
    
    return { week: weekNum, year };
  };

  // Fetch weekly targets when week changes
  const fetchWeeklyTargets = useCallback(async () => {
    if (periodType !== 'week' || !selectedWeek) return;
    
    const [weekNum, weekYear] = selectedWeek.split('-').map(Number);
    if (!weekNum || !weekYear) return;

    try {
      setLoadingTargets(true);
      const response = await axios.get(`${API_URL}/weekly-targets`, {
        params: { week_number: weekNum, year: weekYear },
        headers: getAuthHeader()
      });
      setWeeklyTargets(response.data || []);
    } catch (error) {
      console.error('Error fetching weekly targets:', error);
    } finally {
      setLoadingTargets(false);
    }
  }, [periodType, selectedWeek, getAuthHeader]);

  // Fetch task details for popup
  const fetchTaskDetails = useCallback(async (category, employeeId = null, escalationTier = null) => {
    if (!selectedWeek) return;
    
    const [weekNum, weekYear] = selectedWeek.split('-').map(Number);
    if (!weekNum || !weekYear) return;

    try {
      setTaskDetailsLoading(true);
      setTaskDetailsCategory(category);
      setTaskDetailsFilter('all');
      setTaskDetailsOpen(true);
      
      const params = { week_number: weekNum, year: weekYear };
      if (employeeId) {
        params.employee_id = employeeId;
      }
      if (escalationTier) {
        params.escalation_tier = escalationTier;
      }
      
      const response = await axios.get(`${API_URL}/mis/task-details/${category}`, {
        params,
        headers: getAuthHeader()
      });
      setTaskDetailsData(response.data);
    } catch (error) {
      console.error('Error fetching task details:', error);
      toast.error('Failed to load task details');
      setTaskDetailsOpen(false);
    } finally {
      setTaskDetailsLoading(false);
    }
  }, [selectedWeek, getAuthHeader]);

  // Fetch escalation tasks across all categories for popup
  const fetchEscalationTasks = useCallback(async (tier, employeeId = null) => {
    if (!selectedWeek) return;
    
    const [weekNum, weekYear] = selectedWeek.split('-').map(Number);
    if (!weekNum || !weekYear) return;

    try {
      setEscalationPopupLoading(true);
      setEscalationPopupTier(tier);
      setEscalationPopupOpen(true);
      
      const params = { week_number: weekNum, year: weekYear, escalation_tier: tier };
      if (employeeId) {
        params.employee_id = employeeId;
      }
      
      // Fetch from all categories (delegation, checklist, sws) and combine
      const categories = ['delegation', 'checklist', 'sws'];
      const results = await Promise.all(
        categories.map(cat => 
          axios.get(`${API_URL}/mis/task-details/${cat}`, {
            params,
            headers: getAuthHeader()
          }).catch(() => ({ data: { tasks: [] } }))
        )
      );
      
      // Combine tasks from all categories with category label
      const allTasks = [];
      results.forEach((res, idx) => {
        const categoryName = categories[idx];
        (res.data?.tasks || []).forEach(task => {
          allTasks.push({
            ...task,
            category: categoryName
          });
        });
      });
      
      // Sort by due date
      allTasks.sort((a, b) => {
        const dateA = a.due_date || a.occurrence_date || a.due_at || '';
        const dateB = b.due_date || b.occurrence_date || b.due_at || '';
        return dateA.localeCompare(dateB);
      });
      
      setEscalationPopupData({
        tier,
        employee_id: employeeId,
        employee_name: results[0]?.data?.employee_name,
        week: results[0]?.data?.week,
        tasks: allTasks,
        total: allTasks.length
      });
    } catch (error) {
      console.error('Error fetching escalation tasks:', error);
      toast.error('Failed to load escalation tasks');
      setEscalationPopupOpen(false);
    } finally {
      setEscalationPopupLoading(false);
    }
  }, [selectedWeek, getAuthHeader]);

  // Get filtered tasks based on selected filter
  const filteredTaskDetails = useMemo(() => {
    if (!taskDetailsData?.tasks) return [];
    if (taskDetailsFilter === 'all') return taskDetailsData.tasks;
    return taskDetailsData.tasks.filter(t => t.status === taskDetailsFilter);
  }, [taskDetailsData, taskDetailsFilter]);

  useEffect(() => {
    fetchWeeklyTargets();
  }, [fetchWeeklyTargets]);

  // Save current week's target for selected employee (inline)
  const handleSaveCurrentWeekTarget = async () => {
    if (!selectedEmployee || !currentWeekTargets.target_nd_percent || !currentWeekTargets.target_ndot_percent) {
      toast.error('Please enter both target values');
      return;
    }

    const currentWeek = getCurrentCalendarWeek();
    if (!currentWeek) {
      toast.error('Could not determine current week');
      return;
    }

    try {
      setSavingTargets(true);
      await axios.post(`${API_URL}/weekly-targets`, {
        employee_id: selectedEmployee,
        week_number: currentWeek.week,
        year: currentWeek.year,
        target_nd_percent: parseFloat(currentWeekTargets.target_nd_percent),
        target_ndot_percent: parseFloat(currentWeekTargets.target_ndot_percent)
      }, {
        headers: getAuthHeader()
      });

      toast.success('Current week targets saved successfully');
      setCurrentWeekTargets({ target_nd_percent: '', target_ndot_percent: '' });
      fetchWeeklyTargets();
    } catch (error) {
      console.error('Error saving current week targets:', error);
      toast.error('Failed to save targets');
    } finally {
      setSavingTargets(false);
    }
  };

  // Get target for an employee
  const getEmployeeTarget = (employeeId) => {
    return weeklyTargets.find(t => t.employee_id === employeeId);
  };

  // Filter employees based on search
  const filteredEmployees = useMemo(() => {
    const officeStaff = employees.filter(emp => emp.staff_type === 'Office Staff');
    if (!employeeSearch) return officeStaff.sort((a, b) => a.name?.localeCompare(b.name));
    return officeStaff
      .filter(emp => 
        emp.name?.toLowerCase().includes(employeeSearch.toLowerCase()) ||
        emp.employee_code?.toLowerCase().includes(employeeSearch.toLowerCase())
      )
      .sort((a, b) => a.name?.localeCompare(b.name));
  }, [employees, employeeSearch]);

  // Get selected employee name for display
  const selectedEmployeeName = useMemo(() => {
    if (!selectedEmployee) return '';
    const emp = employees.find(e => e.id === selectedEmployee);
    return emp ? emp.name : '';
  }, [selectedEmployee, employees]);

  // Sort weighted scores based on scoreSortOrder
  const sortedWeightedEmployees = useMemo(() => {
    if (!weightedScores?.employees) return [];
    const filtered = selectedEmployee 
      ? weightedScores.employees.filter(e => e.employee_id === selectedEmployee)
      : weightedScores.employees;
    return [...filtered].sort((a, b) => {
      // In negative scoring: LOWER score = BETTER performance
      // Calculate weighted score: (% ND × 60%) + (% NDOT × 40%)
      const getScore = (emp) => {
        const pctND = emp.pct_not_done ?? emp.overall?.pct_not_done ?? 100;
        const pctNDOT = emp.pct_not_done_on_time ?? emp.overall?.pct_not_done_on_time ?? 100;
        return (pctND * 0.6) + (pctNDOT * 0.4);
      };
      const scoreA = getScore(a);
      const scoreB = getScore(b);
      // 'asc' = Best First (lowest scores at top)
      // 'desc' = Worst First (highest scores at top)
      if (scoreSortOrder === 'asc') {
        return scoreA - scoreB; // Lowest (best) first
      }
      return scoreB - scoreA; // Highest (worst) first
    });
  }, [weightedScores, selectedEmployee, scoreSortOrder]);

  const fetchAvailableWeeks = async () => {
    try {
      const response = await axios.get(`${API_URL}/mis/available-weeks`, { headers: getAuthHeader() });
      setAvailableWeeks(response.data);
      // Set default to current week
      const currentWeek = response.data.find(w => w.is_current);
      if (currentWeek) {
        setSelectedWeek(`${currentWeek.week_number}-${currentWeek.year}`);
      }
    } catch (error) {
      console.error('Failed to fetch weeks:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      // Use the correct endpoint: /hr-employees (not /hr/employees)
      const response = await axios.get(`${API_URL}/hr-employees`, { headers: getAuthHeader() });
      setEmployees(response.data);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    }
  };

  const fetchProductivityData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('period_type', periodType);
      
      if (periodType === 'week' && selectedWeek) {
        const [weekNum, weekYear] = selectedWeek.split('-');
        params.append('week_number', weekNum);
        params.append('week_year', weekYear);
      } else if (periodType === 'month') {
        params.append('month', selectedMonth);
        params.append('year', selectedYear);
      } else if (periodType === 'custom' && customStartDate && customEndDate) {
        params.append('start_date', new Date(customStartDate).toISOString());
        params.append('end_date', new Date(customEndDate).toISOString());
      }
      
      const response = await axios.get(`${API_URL}/mis/employee-productivity-v2?${params}`, { headers: getAuthHeader() });
      setData(response.data);
    } catch (error) {
      toast.error('Failed to fetch productivity data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [periodType, selectedWeek, selectedMonth, selectedYear, customStartDate, customEndDate, getAuthHeader]);

  const fetchProductionEfficiency = useCallback(async () => {
    if (periodType !== 'week' || !selectedWeek) return;
    
    setLoadingEfficiency(true);
    try {
      const [weekNum, weekYear] = selectedWeek.split('-');
      const response = await axios.get(
        `${API_URL}/mis/production-efficiency?week_number=${weekNum}&week_year=${weekYear}&metric_type=${efficiencyMetric}`,
        { headers: getAuthHeader() }
      );
      setProductionEfficiency(response.data);
    } catch (error) {
      console.error('Failed to fetch production efficiency:', error);
    } finally {
      setLoadingEfficiency(false);
    }
  }, [selectedWeek, efficiencyMetric, periodType, getAuthHeader]);

  const fetchWeightedScores = useCallback(async () => {
    if (periodType !== 'week' || !selectedWeek) return;
    
    setLoadingWeighted(true);
    try {
      const [weekNum, weekYear] = selectedWeek.split('-');
      const response = await axios.get(
        `${API_URL}/mis/weighted-scores-v2?week_number=${weekNum}&week_year=${weekYear}`,
        { headers: getAuthHeader() }
      );
      setWeightedScores(response.data);
    } catch (error) {
      console.error('Failed to fetch weighted scores:', error);
    } finally {
      setLoadingWeighted(false);
    }
  }, [selectedWeek, blendFactor, periodType, getAuthHeader]);

  useEffect(() => {
    fetchAvailableWeeks();
    fetchEmployees();
  }, []);

  useEffect(() => {
    if (selectedWeek || periodType !== 'week') {
      fetchProductivityData();
    }
  }, [fetchProductivityData, selectedWeek, periodType]);

  useEffect(() => {
    if (periodType === 'week' && selectedWeek) {
      fetchProductionEfficiency();
    }
  }, [fetchProductionEfficiency, selectedWeek, efficiencyMetric, periodType]);

  useEffect(() => {
    if (periodType === 'week' && selectedWeek) {
      fetchWeightedScores();
    }
  }, [fetchWeightedScores, selectedWeek, blendFactor, periodType]);

  // Calculate employee breakdown when employee is selected (v2 structure)
  useEffect(() => {
    if (selectedEmployee) {
      // Prioritize data.employees from employee-productivity-v2 API (has correct v2 structure)
      let empData = data?.employees?.find(e => e.employee_id === selectedEmployee);
      
      // Fallback to weightedScores if not found
      if (!empData && weightedScores?.employees) {
        empData = weightedScores.employees.find(e => e.employee_id === selectedEmployee);
      }
      
      // If still not found, create empty data for this employee with v2 structure
      if (!empData) {
        const empInfo = employees.find(e => e.id === selectedEmployee);
        if (empInfo) {
          empData = {
            employee_id: selectedEmployee,
            name: empInfo.name,
            employee_code: empInfo.employee_code || '',
            // v2 structure - ND/NDOT metrics (lower is better)
            delegation: { total_due: 0, not_done: 0, not_done_on_time: 0, pct_not_done: 0, pct_not_done_on_time: 0, escalation: {} },
            checklist: { total_due: 0, not_done: 0, not_done_on_time: 0, pct_not_done: 0, pct_not_done_on_time: 0, escalation: {} },
            sws: { total_due: 0, not_done: 0, not_done_on_time: 0, pct_not_done: 0, pct_not_done_on_time: 0, escalation: {} },
            production: null,
            overall: { weighted_score: 0, pct_not_done: 0, pct_not_done_on_time: 0, total_escalations: 0 },
            lifetime_total_tasks: 0,
            no_tasks: true
          };
        }
      }
      
      if (empData) {
        setEmployeeBreakdown(empData);
      }
    } else {
      setEmployeeBreakdown(null);
    }
  }, [selectedEmployee, weightedScores, data, employees]);


  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <BarChart3 className="w-6 h-6" />
              MIS Dashboard
            </h1>
            <p className="text-muted-foreground">Employee Productivity Scorecard</p>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="space-y-2">
                <Label>Period Type</Label>
                <Select value={periodType} onValueChange={setPeriodType}>
                  <SelectTrigger className="w-36">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="week">Weekly</SelectItem>
                    <SelectItem value="month">Monthly</SelectItem>
                    <SelectItem value="custom">Custom Range</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {periodType === 'week' && (
                <div className="space-y-2">
                  <Label>Select Week</Label>
                  <Select value={selectedWeek} onValueChange={setSelectedWeek}>
                    <SelectTrigger className="w-56">
                      <SelectValue placeholder="Select week" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableWeeks.map(w => (
                        <SelectItem key={`${w.week_number}-${w.year}`} value={`${w.week_number}-${w.year}`}>
                          {w.label} ({w.start_date} - {w.end_date})
                          {w.is_current && <Badge className="ml-2 text-xs">Current</Badge>}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {periodType === 'month' && (
                <>
                  <div className="space-y-2">
                    <Label>Month</Label>
                    <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                      <SelectTrigger className="w-36">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MONTHS.map(m => <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Year</Label>
                    <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                      <SelectTrigger className="w-28">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {YEARS.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              {periodType === 'custom' && (
                <>
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      className="w-40"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Input
                      type="date"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      className="w-40"
                    />
                  </div>
                  <Button onClick={fetchProductivityData} disabled={!customStartDate || !customEndDate}>
                    Apply
                  </Button>
                </>
              )}

              {/* Employee Selector for Individual Breakdown - Searchable */}
              {periodType === 'week' && (
                <div className="space-y-2 ml-auto">
                  <Label>Select Employee</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
                    <Input
                      placeholder="Search employee..."
                      value={employeeSearch || selectedEmployeeName}
                      onChange={(e) => {
                        setEmployeeSearch(e.target.value);
                        setShowEmployeeDropdown(true);
                        if (e.target.value === '') {
                          setSelectedEmployee('');
                        }
                      }}
                      onFocus={() => setShowEmployeeDropdown(true)}
                      className="w-64 pl-9"
                      data-testid="employee-search-input"
                    />
                    {showEmployeeDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
                        <div
                          className="px-3 py-2 cursor-pointer hover:bg-gray-100 text-sm font-medium border-b"
                          onClick={() => {
                            setSelectedEmployee('');
                            setEmployeeSearch('');
                            setShowEmployeeDropdown(false);
                          }}
                        >
                          -- All Employees --
                        </div>
                        {filteredEmployees.length > 0 ? (
                          filteredEmployees.map((emp) => {
                            const scoreData = weightedScores?.employees?.find(e => e.employee_id === emp.id);
                            const score = scoreData?.total_weighted_score ?? '-';
                            return (
                              <div
                                key={emp.id}
                                className={`px-3 py-2 cursor-pointer hover:bg-gray-100 text-sm ${selectedEmployee === emp.id ? 'bg-blue-50' : ''}`}
                                onClick={() => {
                                  setSelectedEmployee(emp.id);
                                  setEmployeeSearch('');
                                  setShowEmployeeDropdown(false);
                                }}
                              >
                                <span className="font-medium">{emp.name}</span>
                                <span className="text-muted-foreground ml-2">({score}/100)</span>
                              </div>
                            );
                          })
                        ) : (
                          <div className="px-3 py-2 text-sm text-muted-foreground">No employees found</div>
                        )}
                      </div>
                    )}
                  </div>
                  {selectedEmployee && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => { setSelectedEmployee(''); setEmployeeSearch(''); }}
                      className="text-xs"
                    >
                      Clear selection
                    </Button>
                  )}
                </div>
              )}
            </div>

            {data?.summary?.period && (
              <div className="mt-3 text-sm text-muted-foreground">
                <Calendar className="inline w-4 h-4 mr-1" />
                Showing data for: {formatDate(data.summary.period.start)} - {formatDate(data.summary.period.end)}
                {data.summary.period.week_number && (
                  <Badge variant="outline" className="ml-2">Week {data.summary.period.week_number}</Badge>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-muted-foreground">Loading productivity data...</div>
          </div>
        ) : data ? (
          <>
            {/* Section 2: Summary Numbers Table */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  {employeeBreakdown ? `Summary - ${employeeBreakdown.name || 'Selected Employee'}` : 'Company-wide Summary'}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table className="w-full">
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead className="w-[180px]">Category</TableHead>
                        <TableHead className="text-center w-[100px]">Total Due</TableHead>
                        <TableHead className="text-center w-[120px]">Due This Week</TableHead>
                        <TableHead className="text-center w-[120px]">Due Until Now</TableHead>
                        <TableHead className="text-center w-[80px]">ND</TableHead>
                        <TableHead className="text-center w-[80px]">NDOT</TableHead>
                        <TableHead className="text-center w-[80px]">ESC</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(() => {
                        // Get data source based on whether employee is selected
                        const source = employeeBreakdown || data.summary.totals;
                        
                        const categories = [
                          { key: 'delegation', label: 'Delegation', icon: <ListTodo className="w-4 h-4 text-purple-600" />, color: 'text-purple-800' },
                          { key: 'checklist', label: 'Checklist', icon: <ClipboardList className="w-4 h-4 text-green-600" />, color: 'text-green-800' },
                          { key: 'sws', label: 'SWS', icon: <GitBranch className="w-4 h-4 text-orange-600" />, color: 'text-orange-800' },
                          { key: 'production', label: 'Production/Sale', icon: <Factory className="w-4 h-4 text-blue-600" />, color: 'text-blue-800' }
                        ];
                        
                        // Calculate totals
                        let totalDue = 0, totalDueThisWeek = 0, totalDueUntilNow = 0, totalND = 0, totalNDOT = 0, totalESC = 0;
                        
                        const rows = categories.map(cat => {
                          const catData = source[cat.key] || {};
                          const due = catData.total_due || 0;
                          const dueThisWeek = catData.due_this_week || catData.total_due || 0; // Fallback to total_due if not available
                          const dueUntilNow = catData.due_until_now || catData.total_due || 0;
                          const nd = catData.not_done || 0;
                          const ndot = catData.not_done_on_time || 0;
                          const esc = (catData.escalation?.delayed || 0) + (catData.escalation?.critical || 0) + (catData.escalation?.extreme_critical || 0);
                          
                          totalDue += due;
                          totalDueThisWeek += dueThisWeek;
                          totalDueUntilNow += dueUntilNow;
                          totalND += nd;
                          totalNDOT += ndot;
                          totalESC += esc;
                          
                          return (
                            <TableRow key={cat.key}>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  {cat.icon}
                                  <span className={`font-medium ${cat.color}`}>{cat.label}</span>
                                </div>
                              </TableCell>
                              <TableCell className="text-center">{due}</TableCell>
                              <TableCell className="text-center">{dueThisWeek}</TableCell>
                              <TableCell className="text-center">{dueUntilNow}</TableCell>
                              <TableCell className={`text-center font-bold ${nd > 0 ? 'text-red-600' : 'text-green-600'}`}>{nd}</TableCell>
                              <TableCell className={`text-center font-bold ${ndot > 0 ? 'text-orange-600' : 'text-green-600'}`}>{ndot}</TableCell>
                              <TableCell className={`text-center ${esc > 0 ? 'text-red-600' : ''}`}>{esc || '-'}</TableCell>
                            </TableRow>
                          );
                        });
                        
                        return (
                          <>
                            {rows}
                            <TableRow className="bg-muted/30 font-bold">
                              <TableCell>
                                <span className="font-bold">Total</span>
                              </TableCell>
                              <TableCell className="text-center">{totalDue}</TableCell>
                              <TableCell className="text-center">{totalDueThisWeek}</TableCell>
                              <TableCell className="text-center">{totalDueUntilNow}</TableCell>
                              <TableCell className={`text-center ${totalND > 0 ? 'text-red-600' : 'text-green-600'}`}>{totalND}</TableCell>
                              <TableCell className={`text-center ${totalNDOT > 0 ? 'text-orange-600' : 'text-green-600'}`}>{totalNDOT}</TableCell>
                              <TableCell className={`text-center ${totalESC > 0 ? 'text-red-600' : ''}`}>{totalESC || '-'}</TableCell>
                            </TableRow>
                          </>
                        );
                      })()}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            {/* Section 3: Escalation Breakdown - Shows when employee selected and has escalations */}
            {employeeBreakdown && (employeeBreakdown.overall?.total_escalations > 0) && (
              <Card className="border-red-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                    Escalation Breakdown - {employeeBreakdown.name}
                  </CardTitle>
                  <p className="text-xs text-muted-foreground">Click on a count to view tasks • Percentages based on lifetime total tasks ({employeeBreakdown.lifetime_total_tasks || 0})</p>
                </CardHeader>
                <CardContent>
                  {(() => {
                    const delayedCount = (employeeBreakdown.delegation?.escalation?.delayed || 0) + 
                                        (employeeBreakdown.checklist?.escalation?.delayed || 0) + 
                                        (employeeBreakdown.sws?.escalation?.delayed || 0) +
                                        (employeeBreakdown.production?.escalation?.delayed || 0);
                    const criticalCount = (employeeBreakdown.delegation?.escalation?.critical || 0) + 
                                         (employeeBreakdown.checklist?.escalation?.critical || 0) + 
                                         (employeeBreakdown.sws?.escalation?.critical || 0) +
                                         (employeeBreakdown.production?.escalation?.critical || 0);
                    const extremeCount = (employeeBreakdown.delegation?.escalation?.extreme_critical || 0) + 
                                        (employeeBreakdown.checklist?.escalation?.extreme_critical || 0) + 
                                        (employeeBreakdown.sws?.escalation?.extreme_critical || 0) +
                                        (employeeBreakdown.production?.escalation?.extreme_critical || 0);
                    const lifetimeTotal = employeeBreakdown.lifetime_total_tasks || 0;
                    
                    const delayedPct = lifetimeTotal > 0 ? ((delayedCount / lifetimeTotal) * 100).toFixed(1) : '0.0';
                    const criticalPct = lifetimeTotal > 0 ? ((criticalCount / lifetimeTotal) * 100).toFixed(1) : '0.0';
                    const extremePct = lifetimeTotal > 0 ? ((extremeCount / lifetimeTotal) * 100).toFixed(1) : '0.0';
                    
                    return (
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div 
                          className="p-4 bg-yellow-50 rounded-lg cursor-pointer hover:bg-yellow-100 transition-colors"
                          onClick={() => fetchEscalationTasks('delayed', employeeBreakdown.employee_id)}
                        >
                          <Badge className={`${getEscalationBadge('delayed')} cursor-pointer`}>
                            {delayedCount} Delayed
                          </Badge>
                          <p className="text-sm font-semibold text-yellow-700 mt-2">{delayedPct}%</p>
                          <p className="text-xs text-muted-foreground">1-7 days overdue</p>
                        </div>
                        <div 
                          className="p-4 bg-orange-50 rounded-lg cursor-pointer hover:bg-orange-100 transition-colors"
                          onClick={() => fetchEscalationTasks('critical', employeeBreakdown.employee_id)}
                        >
                          <Badge className={`${getEscalationBadge('critical')} cursor-pointer`}>
                            {criticalCount} Critical
                          </Badge>
                          <p className="text-sm font-semibold text-orange-700 mt-2">{criticalPct}%</p>
                          <p className="text-xs text-muted-foreground">8-14 days overdue</p>
                        </div>
                        <div 
                          className="p-4 bg-red-50 rounded-lg cursor-pointer hover:bg-red-100 transition-colors"
                          onClick={() => fetchEscalationTasks('extreme_critical', employeeBreakdown.employee_id)}
                        >
                          <Badge className={`${getEscalationBadge('extreme_critical')} cursor-pointer`}>
                            {extremeCount} Extreme
                          </Badge>
                          <p className="text-sm font-semibold text-red-700 mt-2">{extremePct}%</p>
                          <p className="text-xs text-muted-foreground">15+ days overdue</p>
                        </div>
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            )}

            {/* Weighted Productivity Scores (Weekly Only) - 6 Uniform Columns */}
            {periodType === 'week' && weightedScores && (
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-purple-600" />
                        Weighted Productivity Scorecard
                      </CardTitle>
                      <CardDescription>
                        Performance in % (ND / NDOT) - Lower is better
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Label className="text-xs whitespace-nowrap">Blend Factor:</Label>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">Volume</span>
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.1"
                            value={blendFactor}
                            onChange={(e) => setBlendFactor(parseFloat(e.target.value))}
                            className="w-24 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                          />
                          <span className="text-xs text-muted-foreground">Business</span>
                          <Badge variant="outline" className="ml-1">{blendFactor.toFixed(1)}</Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {loadingWeighted ? (
                    <div className="text-center py-6 text-muted-foreground">Loading weighted scores...</div>
                  ) : sortedWeightedEmployees.length === 0 ? (
                    <div className="text-center py-8">
                      <Users className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-50" />
                      <p className="text-muted-foreground">
                        {selectedEmployee ? 'Selected employee has no tasks this week' : 'No employees with tasks this week'}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Base Weights Legend + Sort Control */}
                      <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg text-xs">
                        <div className="flex items-center gap-4">
                          <span className="font-medium">Weights by Role:</span>
                          <div className="flex gap-2 text-xs">
                            <Badge className="bg-blue-100 text-blue-800">Shopfloor: P30/F30/D20/C20</Badge>
                            <Badge className="bg-green-100 text-green-800">Sales: F30/D35/C35</Badge>
                            <Badge className="bg-orange-100 text-orange-800">Office: F30/D35/C35</Badge>
                          </div>
                        </div>
                        {!selectedEmployee && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setScoreSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
                            className="flex items-center gap-1"
                            data-testid="sort-scores-btn"
                          >
                            {scoreSortOrder === 'desc' ? (
                              <>
                                <ArrowDown className="w-4 h-4" />
                                Worst First
                              </>
                            ) : (
                              <>
                                <ArrowUp className="w-4 h-4" />
                                Best First
                              </>
                            )}
                          </Button>
                        )}
                      </div>

                      {/* Selected Employee Badge */}
                      {selectedEmployee && (
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-sm">
                            Showing: {selectedEmployeeName}
                          </Badge>
                        </div>
                      )}

                      {/* Employee Scores Table - 6 Uniform Columns */}
                      <div className="border rounded-lg overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-muted/50">
                              <TableHead className="sticky left-0 bg-muted/50">Employee</TableHead>
                              <TableHead 
                                className="text-center cursor-pointer hover:bg-purple-50 transition-colors"
                                onClick={() => fetchTaskDetails('delegation', selectedEmployee || null)}
                              >
                                <div className="flex flex-col items-center">
                                  <span className="underline decoration-dotted">Delegation</span>
                                  <span className="text-xs font-normal text-purple-600">% ND / NDOT</span>
                                </div>
                              </TableHead>
                              <TableHead 
                                className="text-center cursor-pointer hover:bg-green-50 transition-colors"
                                onClick={() => fetchTaskDetails('checklist', selectedEmployee || null)}
                              >
                                <div className="flex flex-col items-center">
                                  <span className="underline decoration-dotted">Checklist</span>
                                  <span className="text-xs font-normal text-green-600">% ND / NDOT</span>
                                </div>
                              </TableHead>
                              <TableHead 
                                className="text-center cursor-pointer hover:bg-orange-50 transition-colors"
                                onClick={() => fetchTaskDetails('sws', selectedEmployee || null)}
                              >
                                <div className="flex flex-col items-center">
                                  <span className="underline decoration-dotted">SWS</span>
                                  <span className="text-xs font-normal text-orange-600">% ND / NDOT</span>
                                </div>
                              </TableHead>
                              <TableHead className="text-center">
                                <div className="flex flex-col items-center">
                                  <span>Prod/Sales</span>
                                  <span className="text-xs font-normal text-blue-600">% ND / NDOT</span>
                                </div>
                              </TableHead>
                              <TableHead className="text-center bg-indigo-50">
                                <div className="flex flex-col items-center">
                                  <span className="font-semibold">Weekly</span>
                                  <span className="text-xs font-normal text-indigo-600">Weighted Score</span>
                                </div>
                              </TableHead>
                              <TableHead className="text-center bg-purple-50">
                                <div className="flex flex-col items-center">
                                  <span className="font-semibold">Overall</span>
                                  <span className="text-xs font-normal text-purple-600">Weighted Score</span>
                                </div>
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {sortedWeightedEmployees.map((emp, idx) => {
                              const weeklyScore = calculateWeightedScore(emp);
                              return (
                              <TableRow 
                                key={emp.employee_id} 
                                className={`${idx === 0 && scoreSortOrder === 'asc' ? 'bg-green-50' : ''} cursor-pointer hover:bg-gray-50`}
                                onClick={() => setSelectedEmployee(emp.employee_id)}
                              >
                                <TableCell className="sticky left-0 bg-white">
                                  <div>
                                    <p className="font-medium flex items-center gap-1">
                                      {idx === 0 && scoreSortOrder === 'asc' && <span className="text-green-500">🏆</span>}
                                      {emp.name}
                                    </p>
                                    <p className="text-xs text-muted-foreground">{emp.employee_code}</p>
                                  </div>
                                </TableCell>
                                {['delegation', 'checklist', 'sws', 'production'].map((seg) => (
                                  <TableCell key={seg} className="text-center">
                                    {emp.breakdown?.[seg]?.total_due > 0 ? (
                                      <div className="space-y-1">
                                        <div className="flex justify-center gap-1 items-center">
                                          <span className={`font-bold ${getNegativeScoreColor(emp.breakdown[seg].pct_not_done || 0)}`}>
                                            {emp.breakdown[seg].pct_not_done || 0}%
                                          </span>
                                          <span className="text-gray-400">/</span>
                                          <span className={`text-sm ${getNegativeScoreColor(emp.breakdown[seg].pct_not_done_on_time || 0)}`}>
                                            {emp.breakdown[seg].pct_not_done_on_time || 0}%
                                          </span>
                                        </div>
                                        <p className="text-xs text-muted-foreground">
                                          {emp.breakdown[seg].total_due} due
                                        </p>
                                      </div>
                                    ) : (
                                      <span className="text-muted-foreground">-</span>
                                    )}
                                  </TableCell>
                                ))}
                                <TableCell className="text-center bg-indigo-50/50">
                                  {weeklyScore !== null ? (
                                    <div className="space-y-1">
                                      <div className={`text-xl font-bold ${getNegativeScoreColor(weeklyScore)}`}>
                                        {weeklyScore}%
                                      </div>
                                      <p className="text-xs text-muted-foreground">This Week</p>
                                    </div>
                                  ) : (
                                    <span className="text-muted-foreground">-</span>
                                  )}
                                </TableCell>
                                <TableCell className="text-center bg-purple-50/50">
                                  {weeklyScore !== null ? (
                                    <div className="space-y-1">
                                      <div className={`text-xl font-bold ${getNegativeScoreColor(weeklyScore)}`}>
                                        {weeklyScore}%
                                      </div>
                                      <p className="text-xs text-muted-foreground">Cumulative</p>
                                    </div>
                                  ) : (
                                    <span className="text-muted-foreground">-</span>
                                  )}
                                </TableCell>
                              </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>

                      {/* Algorithm Explanation */}
                      <div className="text-xs text-muted-foreground p-3 bg-muted/20 rounded-lg">
                        <p className="font-medium mb-1">Weighted Score Formula:</p>
                        <p>(Weighted % ND × 60%) + (Weighted % NDOT × 40%) = Weighted Score</p>
                        <p className="mt-1">Lower score = Better performance (0% is perfect)</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Weekly Targets - Inline Section for Employee Meeting */}
            {employeeBreakdown && periodType === 'week' && (
              <Card className="border-indigo-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Target className="w-5 h-5 text-indigo-600" />
                    <span className="font-semibold text-indigo-800">Weekly Targets & Performance - {employeeBreakdown.name}</span>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-2 font-medium text-gray-700">Week</th>
                          <th className="text-center py-2 px-2 font-medium text-gray-700">Target ND%</th>
                          <th className="text-center py-2 px-2 font-medium text-gray-700">Target NDOT%</th>
                          <th className="text-center py-2 px-2 font-medium text-gray-700">Actual ND%</th>
                          <th className="text-center py-2 px-2 font-medium text-gray-700">Actual NDOT%</th>
                          <th className="text-center py-2 px-2 font-medium text-gray-700">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Selected Week Row - Shows performance for the week selected in dropdown */}
                        {(() => {
                          const [weekNum, year] = selectedWeek ? selectedWeek.split('-').map(Number) : [null, null];
                          const selectedWeekTarget = getEmployeeTarget(selectedEmployee);
                          const actualND = employeeBreakdown.pct_not_done ?? employeeBreakdown.overall?.pct_not_done ?? null;
                          const actualNDOT = employeeBreakdown.pct_not_done_on_time ?? employeeBreakdown.overall?.pct_not_done_on_time ?? null;
                          const metND = selectedWeekTarget && actualND !== null && actualND <= selectedWeekTarget.target_nd_percent;
                          const metNDOT = selectedWeekTarget && actualNDOT !== null && actualNDOT <= selectedWeekTarget.target_ndot_percent;
                          
                          return (
                            <tr className="border-b bg-white">
                              <td className="py-3 px-2">
                                <div>
                                  <span className="font-medium">Week-{weekNum}, {year}</span>
                                  <span className="text-xs text-muted-foreground block">
                                    ({weekNum && year ? getWeekDateRange(weekNum, year) : '-'})
                                  </span>
                                </div>
                              </td>
                              <td className="text-center py-3 px-2">
                                {selectedWeekTarget ? (
                                  <Badge variant="outline" className="bg-indigo-50 text-indigo-700">
                                    ≤ {selectedWeekTarget.target_nd_percent}%
                                  </Badge>
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </td>
                              <td className="text-center py-3 px-2">
                                {selectedWeekTarget ? (
                                  <Badge variant="outline" className="bg-indigo-50 text-indigo-700">
                                    ≤ {selectedWeekTarget.target_ndot_percent}%
                                  </Badge>
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </td>
                              <td className="text-center py-3 px-2">
                                {actualND !== null ? (
                                  <span className={`font-bold ${selectedWeekTarget ? (metND ? 'text-green-600' : 'text-red-600') : getNegativeScoreColor(actualND)}`}>
                                    {actualND.toFixed(0)}%
                                  </span>
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </td>
                              <td className="text-center py-3 px-2">
                                {actualNDOT !== null ? (
                                  <span className={`font-bold ${selectedWeekTarget ? (metNDOT ? 'text-green-600' : 'text-red-600') : getNegativeScoreColor(actualNDOT)}`}>
                                    {actualNDOT.toFixed(0)}%
                                  </span>
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </td>
                              <td className="text-center py-3 px-2">
                                {selectedWeekTarget && actualND !== null && actualNDOT !== null ? (
                                  metND && metNDOT ? (
                                    <Badge className="bg-green-100 text-green-800 text-xs">✓ Met</Badge>
                                  ) : (
                                    <Badge className="bg-red-100 text-red-800 text-xs">✗ Not Met</Badge>
                                  )
                                ) : (
                                  <span className="text-xs text-muted-foreground">-</span>
                                )}
                              </td>
                            </tr>
                          );
                        })()}
                        
                        {/* Current Calendar Week Row - For setting targets */}
                        {(() => {
                          const currentWeek = getCurrentCalendarWeek();
                          return (
                            <tr className="bg-indigo-50/50">
                              <td className="py-3 px-2">
                                <div>
                                  <span className="font-medium text-indigo-700">Current Week&apos;s Targets</span>
                                  <span className="text-xs text-muted-foreground block">
                                    Week-{currentWeek?.week}, {currentWeek?.year} ({currentWeek ? getWeekDateRange(currentWeek.week, currentWeek.year) : '-'})
                                  </span>
                                </div>
                              </td>
                              <td className="text-center py-3 px-2">
                                <Input
                                  type="number"
                                  min="0"
                                  max="100"
                                  placeholder="0-100"
                                  value={currentWeekTargets.target_nd_percent}
                                  onChange={(e) => setCurrentWeekTargets(prev => ({ ...prev, target_nd_percent: e.target.value }))}
                                  className="w-20 h-8 text-center mx-auto"
                                />
                              </td>
                              <td className="text-center py-3 px-2">
                                <Input
                                  type="number"
                                  min="0"
                                  max="100"
                                  placeholder="0-100"
                                  value={currentWeekTargets.target_ndot_percent}
                                  onChange={(e) => setCurrentWeekTargets(prev => ({ ...prev, target_ndot_percent: e.target.value }))}
                                  className="w-20 h-8 text-center mx-auto"
                                />
                              </td>
                              <td className="text-center py-3 px-2 text-muted-foreground">-</td>
                              <td className="text-center py-3 px-2 text-muted-foreground">-</td>
                              <td className="text-center py-3 px-2">
                                <Button
                                  size="sm"
                                  onClick={handleSaveCurrentWeekTarget}
                                  disabled={savingTargets || !currentWeekTargets.target_nd_percent || !currentWeekTargets.target_ndot_percent}
                                  className="h-8"
                                >
                                  {savingTargets ? 'Saving...' : 'Save'}
                                </Button>
                              </td>
                            </tr>
                          );
                        })()}
                      </tbody>
                    </table>
                  </div>
                  
                  <p className="text-xs text-muted-foreground mt-2">
                    * Employee declares their own target for current week during the meeting
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Negative Scoring Legend */}
            <Card className="bg-gray-50">
              <CardContent className="p-4">
                <p className="text-sm font-medium mb-2">Negative Scoring Legend (Lower = Better)</p>
                <div className="flex flex-wrap gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-600"></div>
                    <span>0-10% Excellent</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <span>11-25% Good</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                    <span>26-40% Needs Attention</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-600"></div>
                    <span>&gt;40% Critical</span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  <strong>ND</strong> = % Not Done (task never completed - most serious) | 
                  <strong>NOT</strong> = % Not On Time (task done late or not done) | 
                  <strong>Sorted by:</strong> % Not Done (Critical tasks first)
                </p>
              </CardContent>
            </Card>

            {/* Production Summary (if any) */}
            {(data.summary?.totals?.production?.total_due > 0) && (
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Factory className="w-4 h-4 text-indigo-600" />
                    <span className="font-medium">Production Summary</span>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Total Due</p>
                      <p className="text-lg font-bold">{(data.summary.totals.production.total_due || 0).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Not Done On Time</p>
                      <p className="text-lg font-bold">{(data.summary.totals.production.not_done_on_time || 0).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">% Not On Time</p>
                      <p className={`text-lg font-bold ${getScoreColor(data.summary.totals.production?.pct_not_done_on_time || 0, true)}`}>
                        {data.summary.totals.production?.pct_not_done_on_time || 0}%
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Production Efficiency Section (Weekly Only) */}
            {periodType === 'week' && productionEfficiency && (
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-green-600" />
                        Production Efficiency (On-Time %)
                      </CardTitle>
                      <CardDescription>{productionEfficiency.metric_description}</CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs">Metric:</Label>
                      <Select value={efficiencyMetric} onValueChange={setEfficiencyMetric}>
                        <SelectTrigger className="w-48 h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="planned_vs_actual">Planned vs Actual (Week)</SelectItem>
                          <SelectItem value="benchmark_date">Benchmark Date</SelectItem>
                          <SelectItem value="target_date">SO Target Date (FNG)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {loadingEfficiency ? (
                    <div className="text-center py-6 text-muted-foreground">Loading efficiency data...</div>
                  ) : (
                    <div className="space-y-4">
                      {/* Overall Summary */}
                      <div className="grid grid-cols-4 gap-4 p-4 bg-muted/30 rounded-lg">
                        <div>
                          <p className="text-xs text-muted-foreground">Total Items</p>
                          <p className="text-lg font-bold">{productionEfficiency.summary.total_items}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">On-Time</p>
                          <p className="text-lg font-bold text-green-600">{productionEfficiency.summary.on_time_count}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">On-Time %</p>
                          <p className={`text-lg font-bold ${getScoreColor(productionEfficiency.summary.on_time_pct)}`}>
                            {productionEfficiency.summary.on_time_pct}%
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Completion %</p>
                          <p className={`text-lg font-bold ${getScoreColor(productionEfficiency.summary.completion_pct)}`}>
                            {productionEfficiency.summary.completion_pct}%
                          </p>
                        </div>
                      </div>

                      {/* By Supervisor Table */}
                      {productionEfficiency.by_supervisor.length > 0 && (
                        <div>
                          <h4 className="font-medium mb-2 text-sm">By Supervisor</h4>
                          <div className="border rounded-lg overflow-hidden">
                            <Table>
                              <TableHeader>
                                <TableRow className="bg-muted/50">
                                  <TableHead>Supervisor</TableHead>
                                  <TableHead className="text-right">Items</TableHead>
                                  <TableHead className="text-right">Planned</TableHead>
                                  <TableHead className="text-right">Actual</TableHead>
                                  <TableHead className="text-right">On-Time</TableHead>
                                  <TableHead className="text-right">Late</TableHead>
                                  <TableHead className="text-right">Not Done</TableHead>
                                  <TableHead className="text-right">On-Time %</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {productionEfficiency.by_supervisor.map((sup) => (
                                  <TableRow key={sup.supervisor_id}>
                                    <TableCell className="font-medium">{sup.supervisor_name}</TableCell>
                                    <TableCell className="text-right">{sup.total_items}</TableCell>
                                    <TableCell className="text-right">{sup.total_planned.toLocaleString()}</TableCell>
                                    <TableCell className="text-right">{sup.total_actual.toLocaleString()}</TableCell>
                                    <TableCell className="text-right text-green-600">{sup.on_time_count}</TableCell>
                                    <TableCell className="text-right text-yellow-600">{sup.late_count}</TableCell>
                                    <TableCell className="text-right text-red-600">{sup.not_done_count}</TableCell>
                                    <TableCell className="text-right">
                                      <Badge className={getScoreBadge(sup.on_time_pct)}>{sup.on_time_pct}%</Badge>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                      )}

                      {/* By Process Table */}
                      {productionEfficiency.by_process.length > 0 && (
                        <div>
                          <h4 className="font-medium mb-2 text-sm">By Process</h4>
                          <div className="border rounded-lg overflow-hidden">
                            <Table>
                              <TableHeader>
                                <TableRow className="bg-muted/50">
                                  <TableHead>Process</TableHead>
                                  <TableHead className="text-right">Items</TableHead>
                                  <TableHead className="text-right">Planned</TableHead>
                                  <TableHead className="text-right">Actual</TableHead>
                                  <TableHead className="text-right">On-Time</TableHead>
                                  <TableHead className="text-right">On-Time %</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {productionEfficiency.by_process.map((proc) => (
                                  <TableRow key={proc.process_name}>
                                    <TableCell className="font-medium">{proc.process_name}</TableCell>
                                    <TableCell className="text-right">{proc.total_items}</TableCell>
                                    <TableCell className="text-right">{proc.total_planned.toLocaleString()}</TableCell>
                                    <TableCell className="text-right">{proc.total_actual.toLocaleString()}</TableCell>
                                    <TableCell className="text-right text-green-600">{proc.on_time_count}</TableCell>
                                    <TableCell className="text-right">
                                      <Badge className={getScoreBadge(proc.on_time_pct)}>{proc.on_time_pct}%</Badge>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                      )}

                      {productionEfficiency.by_supervisor.length === 0 && (
                        <div className="text-center py-6 text-muted-foreground">
                          No production plans found for this week
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Employee-wise Productivity Table - 6 Uniform Columns */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">Employee-wise Productivity</CardTitle>
                    <CardDescription>6 columns: Delegation, Checklist, SWS, Prod/Sales, Weekly Score, Overall Score</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Sort Direction Toggle */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEmployeeTableSortOrder(employeeTableSortOrder === 'asc' ? 'desc' : 'asc')}
                      className="flex items-center gap-1"
                      data-testid="employee-productivity-sort-direction-btn"
                    >
                      {employeeTableSortOrder === 'asc' ? (
                        <>
                          <ArrowUp className="w-3 h-3" />
                          Best First
                        </>
                      ) : (
                        <>
                          <ArrowDown className="w-3 h-3" />
                          Worst First
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {data.employees.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                    <p className="text-muted-foreground">No employee productivity data for this period</p>
                    <p className="text-sm text-muted-foreground">Tasks may not be assigned or due dates fall outside this range</p>
                  </div>
                ) : (
                  <div className="border rounded-lg overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/50">
                          <TableHead className="sticky left-0 bg-muted/50">Employee</TableHead>
                          <TableHead className="text-center">
                            <div className="flex flex-col items-center">
                              <span>Delegation</span>
                              <span className="text-xs font-normal text-purple-600">% ND / NDOT</span>
                            </div>
                          </TableHead>
                          <TableHead className="text-center">
                            <div className="flex flex-col items-center">
                              <span>Checklist</span>
                              <span className="text-xs font-normal text-green-600">% ND / NDOT</span>
                            </div>
                          </TableHead>
                          <TableHead className="text-center">
                            <div className="flex flex-col items-center">
                              <span>SWS</span>
                              <span className="text-xs font-normal text-orange-600">% ND / NDOT</span>
                            </div>
                          </TableHead>
                          <TableHead className="text-center">
                            <div className="flex flex-col items-center">
                              <span>Prod/Sales</span>
                              <span className="text-xs font-normal text-blue-600">% ND / NDOT</span>
                            </div>
                          </TableHead>
                          <TableHead 
                            className={`text-center cursor-pointer hover:bg-indigo-100 ${employeeTableSortColumn === 'weekly' ? 'bg-indigo-100' : 'bg-indigo-50'}`}
                            onClick={() => setEmployeeTableSortColumn('weekly')}
                          >
                            <div className="flex flex-col items-center">
                              <span className="font-semibold flex items-center gap-1">
                                Weekly
                                {employeeTableSortColumn === 'weekly' && (
                                  employeeTableSortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                                )}
                              </span>
                              <span className="text-xs font-normal text-indigo-600">Weighted Score</span>
                            </div>
                          </TableHead>
                          <TableHead 
                            className={`text-center cursor-pointer hover:bg-purple-100 ${employeeTableSortColumn === 'overall' ? 'bg-purple-100' : 'bg-purple-50'}`}
                            onClick={() => setEmployeeTableSortColumn('overall')}
                          >
                            <div className="flex flex-col items-center">
                              <span className="font-semibold flex items-center gap-1">
                                Overall
                                {employeeTableSortColumn === 'overall' && (
                                  employeeTableSortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                                )}
                              </span>
                              <span className="text-xs font-normal text-purple-600">Weighted Score</span>
                            </div>
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {[...data.employees]
                          .map(emp => ({
                            ...emp,
                            weeklyScore: calculateWeightedScore(emp),
                            overallScore: calculateWeightedScore(emp) // For now, same as weekly until we have historical data
                          }))
                          .sort((a, b) => {
                            const scoreA = employeeTableSortColumn === 'weekly' ? (a.weeklyScore ?? 100) : (a.overallScore ?? 100);
                            const scoreB = employeeTableSortColumn === 'weekly' ? (b.weeklyScore ?? 100) : (b.overallScore ?? 100);
                            return employeeTableSortOrder === 'asc' ? scoreA - scoreB : scoreB - scoreA;
                          })
                          .map((emp, idx) => (
                          <TableRow key={emp.employee_id} className={idx === 0 && employeeTableSortOrder === 'asc' ? 'bg-green-50' : ''}>
                            <TableCell className="sticky left-0 bg-white">
                              <div>
                                <p className="font-medium flex items-center gap-1">
                                  {idx === 0 && employeeTableSortOrder === 'asc' && <span className="text-green-500">🏆</span>}
                                  {emp.name}
                                </p>
                                <p className="text-xs text-muted-foreground">{emp.employee_code}</p>
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              {(emp.delegation?.total_due || 0) > 0 ? (
                                <div className="flex justify-center gap-1 items-center">
                                  <span className={`font-bold ${getNegativeScoreColor(emp.delegation.pct_not_done || 0)}`}>
                                    {emp.delegation.pct_not_done || 0}%
                                  </span>
                                  <span className="text-gray-400">/</span>
                                  <span className={`text-sm ${getNegativeScoreColor(emp.delegation.pct_not_done_on_time || 0)}`}>
                                    {emp.delegation.pct_not_done_on_time || 0}%
                                  </span>
                                </div>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              {(emp.checklist?.total_due || 0) > 0 ? (
                                <div className="flex justify-center gap-1 items-center">
                                  <span className={`font-bold ${getNegativeScoreColor(emp.checklist.pct_not_done || 0)}`}>
                                    {emp.checklist.pct_not_done || 0}%
                                  </span>
                                  <span className="text-gray-400">/</span>
                                  <span className={`text-sm ${getNegativeScoreColor(emp.checklist.pct_not_done_on_time || 0)}`}>
                                    {emp.checklist.pct_not_done_on_time || 0}%
                                  </span>
                                </div>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              {(emp.sws?.total_due || 0) > 0 ? (
                                <div className="flex justify-center gap-1 items-center">
                                  <span className={`font-bold ${getNegativeScoreColor(emp.sws.pct_not_done || 0)}`}>
                                    {emp.sws.pct_not_done || 0}%
                                  </span>
                                  <span className="text-gray-400">/</span>
                                  <span className={`text-sm ${getNegativeScoreColor(emp.sws.pct_not_done_on_time || 0)}`}>
                                    {emp.sws.pct_not_done_on_time || 0}%
                                  </span>
                                </div>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              {(emp.production?.total_due || 0) > 0 ? (
                                <div className="flex justify-center gap-1 items-center">
                                  <span className={`font-bold ${getNegativeScoreColor(emp.production.pct_not_done || 0)}`}>
                                    {emp.production.pct_not_done || 0}%
                                  </span>
                                  <span className="text-gray-400">/</span>
                                  <span className={`text-sm ${getNegativeScoreColor(emp.production.pct_not_done_on_time || 0)}`}>
                                    {emp.production.pct_not_done_on_time || 0}%
                                  </span>
                                </div>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                            <TableCell className={`text-center ${employeeTableSortColumn === 'weekly' ? 'bg-indigo-50' : ''}`}>
                              {emp.weeklyScore !== null ? (
                                <Badge className={getScoreBadge(100 - emp.weeklyScore)}>
                                  {emp.weeklyScore}%
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                            <TableCell className={`text-center ${employeeTableSortColumn === 'overall' ? 'bg-purple-50' : ''}`}>
                              {emp.overallScore !== null ? (
                                <div className="space-y-1">
                                  <Badge className={getScoreBadge(100 - emp.overallScore)}>
                                    {emp.overallScore}%
                                  </Badge>
                                  {(emp.overall?.total_escalations || 0) > 0 && (
                                    <p className="text-xs text-red-600">{emp.overall.total_escalations} esc.</p>
                                  )}
                                </div>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>

          </>
        ) : null}
      </div>

      {/* Task Details Dialog */}
      <Dialog open={taskDetailsOpen} onOpenChange={setTaskDetailsOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {taskDetailsCategory === 'delegation' && <ListTodo className="w-5 h-5 text-purple-600" />}
              {taskDetailsCategory === 'checklist' && <ClipboardList className="w-5 h-5 text-green-600" />}
              {taskDetailsCategory === 'sws' && <GitBranch className="w-5 h-5 text-orange-600" />}
              {taskDetailsCategory?.charAt(0).toUpperCase() + taskDetailsCategory?.slice(1)} Tasks
              {taskDetailsData?.week && (
                <span className="text-sm font-normal text-muted-foreground ml-2">
                  - Week {taskDetailsData.week.number}, {taskDetailsData.week.year} ({taskDetailsData.week.start} - {taskDetailsData.week.end})
                </span>
              )}
            </DialogTitle>
            <DialogDescription>
              {taskDetailsData?.employee_name ? (
                <span>Showing tasks for <strong>{taskDetailsData.employee_name}</strong></span>
              ) : (
                <span>Showing tasks for all employees</span>
              )}
            </DialogDescription>
          </DialogHeader>

          {taskDetailsLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : taskDetailsData ? (
            <div className="flex-1 overflow-hidden flex flex-col gap-4">
              {/* Summary Tabs */}
              <div className="flex gap-2 flex-wrap">
                <Button
                  variant={taskDetailsFilter === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTaskDetailsFilter('all')}
                  className="gap-1"
                >
                  Total
                  <Badge variant="secondary" className="ml-1">({taskDetailsData.summary.total})</Badge>
                </Button>
                <Button
                  variant={taskDetailsFilter === 'done' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTaskDetailsFilter('done')}
                  className="gap-1 border-green-200 hover:bg-green-50"
                >
                  <CheckCircle2 className="w-3 h-3 text-green-600" />
                  Done
                  <Badge variant="secondary" className="ml-1 bg-green-100 text-green-700">({taskDetailsData.summary.done})</Badge>
                </Button>
                <Button
                  variant={taskDetailsFilter === 'ndot' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTaskDetailsFilter('ndot')}
                  className="gap-1 border-yellow-200 hover:bg-yellow-50"
                >
                  <Clock className="w-3 h-3 text-yellow-600" />
                  NDOT (Late)
                  <Badge variant="secondary" className="ml-1 bg-yellow-100 text-yellow-700">({taskDetailsData.summary.ndot})</Badge>
                </Button>
                <Button
                  variant={taskDetailsFilter === 'nd' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTaskDetailsFilter('nd')}
                  className="gap-1 border-red-200 hover:bg-red-50"
                >
                  <AlertTriangle className="w-3 h-3 text-red-600" />
                  ND (Pending)
                  <Badge variant="secondary" className="ml-1 bg-red-100 text-red-700">({taskDetailsData.summary.nd})</Badge>
                </Button>
              </div>

              {/* Task Counts & Scoring Formula */}
              {taskDetailsData.task_counts && taskDetailsData.scoring && (
                <div className="bg-muted/30 p-3 rounded-lg text-sm space-y-1">
                  <div className="flex gap-4 flex-wrap">
                    <span><strong>Week Tasks:</strong> {taskDetailsData.task_counts.week_tasks}</span>
                    <span><strong>Rolled (R/O):</strong> {taskDetailsData.task_counts.rolled_tasks}</span>
                    <span><strong>Total:</strong> {taskDetailsData.task_counts.total}</span>
                  </div>
                  <div className="flex gap-4 flex-wrap text-muted-foreground">
                    <span>{taskDetailsData.scoring.formula_ndot}</span>
                    <span>{taskDetailsData.scoring.formula_nd}</span>
                  </div>
                </div>
              )}

              {/* Task Table */}
              <div className="flex-1 overflow-auto border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="text-center w-12">R/O</TableHead>
                      {!taskDetailsData.employee_id && (
                        <TableHead>Employee</TableHead>
                      )}
                      {taskDetailsCategory === 'delegation' && (
                        <>
                          <TableHead className="text-center w-12">IMP</TableHead>
                          <TableHead>Title</TableHead>
                          <TableHead>Assigned By</TableHead>
                          <TableHead className="text-center">Due Date</TableHead>
                          <TableHead className="text-center">Done On</TableHead>
                        </>
                      )}
                      {taskDetailsCategory === 'checklist' && (
                        <>
                          <TableHead>Template Name</TableHead>
                          <TableHead className="text-center">Occurrence Date</TableHead>
                          <TableHead className="text-center">Done On</TableHead>
                        </>
                      )}
                      {taskDetailsCategory === 'sws' && (
                        <>
                          <TableHead>Step Name</TableHead>
                          <TableHead>Workflow</TableHead>
                          <TableHead className="text-center">Due At</TableHead>
                          <TableHead className="text-center">Done At</TableHead>
                        </>
                      )}
                      <TableHead className="text-center w-20">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredTaskDetails.length === 0 ? (
                      <TableRow>
                        <TableCell 
                          colSpan={
                            taskDetailsCategory === 'delegation' 
                              ? (taskDetailsData.employee_id ? 7 : 8)  // +1 for IMP column
                              : (taskDetailsData.employee_id ? 6 : 7)
                          } 
                          className="text-center py-8 text-muted-foreground"
                        >
                          No tasks found for this filter
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredTaskDetails.map((task, idx) => (
                        <TableRow 
                          key={task.id || idx}
                          className={
                            task.status === 'done' ? 'bg-green-50/50' :
                            task.status === 'ndot' ? 'bg-yellow-50/50' :
                            'bg-red-50/50'
                          }
                        >
                          <TableCell className="text-center">
                            {task.is_rolled ? (
                              <Badge variant="outline" className="bg-purple-100 text-purple-700 text-xs">R/O</Badge>
                            ) : '-'}
                          </TableCell>
                          {!taskDetailsData.employee_id && (
                            <TableCell className="font-medium">{task.employee_name}</TableCell>
                          )}
                          {taskDetailsCategory === 'delegation' && (
                            <>
                              <TableCell className="text-center">
                                {task.is_imp && (
                                  <Badge className="bg-red-100 text-red-700 border-red-200 text-xs">IMP</Badge>
                                )}
                              </TableCell>
                              <TableCell className="max-w-[200px] truncate" title={task.title}>{task.title}</TableCell>
                              <TableCell>{task.assigned_by}</TableCell>
                              <TableCell className="text-center">{task.due_date}</TableCell>
                              <TableCell className="text-center">{task.done_on || '-'}</TableCell>
                            </>
                          )}
                          {taskDetailsCategory === 'checklist' && (
                            <>
                              <TableCell className="max-w-[200px] truncate" title={task.title}>{task.title}</TableCell>
                              <TableCell className="text-center">{task.occurrence_date}</TableCell>
                              <TableCell className="text-center">{task.done_on || '-'}</TableCell>
                            </>
                          )}
                          {taskDetailsCategory === 'sws' && (
                            <>
                              <TableCell className="max-w-[200px] truncate" title={task.step_name}>{task.step_name}</TableCell>
                              <TableCell className="max-w-[150px] truncate" title={task.workflow_name}>{task.workflow_name}</TableCell>
                              <TableCell className="text-center text-sm">{task.due_at}</TableCell>
                              <TableCell className="text-center text-sm">{task.done_at || '-'}</TableCell>
                            </>
                          )}
                          <TableCell className="text-center">
                            {task.status === 'done' && (
                              <Badge className="bg-green-100 text-green-700 border-green-200">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Done
                              </Badge>
                            )}
                            {task.status === 'ndot' && (
                              <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">
                                <Clock className="w-3 h-3 mr-1" />
                                Late
                              </Badge>
                            )}
                            {task.status === 'nd' && (
                              <Badge className="bg-red-100 text-red-700 border-red-200">
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                Pending
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Escalation Tasks Dialog */}
      <Dialog open={escalationPopupOpen} onOpenChange={setEscalationPopupOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className={`w-5 h-5 ${
                escalationPopupTier === 'delayed' ? 'text-yellow-600' :
                escalationPopupTier === 'critical' ? 'text-orange-600' : 'text-red-600'
              }`} />
              {escalationPopupTier === 'delayed' && 'Delayed Tasks (1-7 days overdue)'}
              {escalationPopupTier === 'critical' && 'Critical Tasks (8-14 days overdue)'}
              {escalationPopupTier === 'extreme_critical' && 'Extreme Critical Tasks (15+ days overdue)'}
              {escalationPopupData?.week && (
                <span className="text-sm font-normal text-muted-foreground ml-2">
                  - Week {escalationPopupData.week.number}, {escalationPopupData.week.year}
                </span>
              )}
            </DialogTitle>
            <DialogDescription>
              {escalationPopupData?.employee_name ? (
                <span>Showing escalated tasks for <strong>{escalationPopupData.employee_name}</strong></span>
              ) : (
                <span>Showing escalated tasks for all employees</span>
              )}
            </DialogDescription>
          </DialogHeader>

          {escalationPopupLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : escalationPopupData ? (
            <div className="flex-1 overflow-hidden flex flex-col gap-4">
              {/* Summary */}
              <div className="bg-muted/30 p-3 rounded-lg text-sm">
                <span><strong>Total Escalated Tasks:</strong> {escalationPopupData.total}</span>
              </div>

              {/* Task Table */}
              <div className="flex-1 overflow-auto border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead>Category</TableHead>
                      {!escalationPopupData.employee_id && (
                        <TableHead>Employee</TableHead>
                      )}
                      <TableHead>Title</TableHead>
                      <TableHead className="text-center">Due</TableHead>
                      <TableHead className="text-center">Done On</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {escalationPopupData.tasks.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                          No tasks found for this escalation tier
                        </TableCell>
                      </TableRow>
                    ) : (
                      escalationPopupData.tasks.map((task) => (
                        <TableRow key={`${task.category}-${task.id}`} className="hover:bg-muted/30">
                          <TableCell>
                            <Badge variant="outline" className={
                              task.category === 'delegation' ? 'bg-purple-50 text-purple-700 border-purple-200' :
                              task.category === 'checklist' ? 'bg-green-50 text-green-700 border-green-200' :
                              'bg-orange-50 text-orange-700 border-orange-200'
                            }>
                              {task.category === 'delegation' && <ListTodo className="w-3 h-3 mr-1" />}
                              {task.category === 'checklist' && <ClipboardList className="w-3 h-3 mr-1" />}
                              {task.category === 'sws' && <GitBranch className="w-3 h-3 mr-1" />}
                              {task.category?.charAt(0).toUpperCase() + task.category?.slice(1)}
                            </Badge>
                          </TableCell>
                          {!escalationPopupData.employee_id && (
                            <TableCell className="max-w-[120px] truncate" title={task.employee_name}>
                              {task.employee_name}
                            </TableCell>
                          )}
                          <TableCell className="max-w-[200px] truncate" title={task.title || task.step_name}>
                            {task.title || task.step_name}
                          </TableCell>
                          <TableCell className="text-center">
                            {task.due_date || task.occurrence_date || task.due_at}
                          </TableCell>
                          <TableCell className="text-center">
                            {task.done_on || task.done_at || '-'}
                          </TableCell>
                          <TableCell className="text-center">
                            {task.status === 'done' && (
                              <Badge className="bg-green-100 text-green-700 border-green-200">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Done
                              </Badge>
                            )}
                            {task.status === 'ndot' && (
                              <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">
                                <Clock className="w-3 h-3 mr-1" />
                                Late
                              </Badge>
                            )}
                            {task.status === 'nd' && (
                              <Badge className="bg-red-100 text-red-700 border-red-200">
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                Pending
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
